import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Pause, Play, Truck, CheckCircle2, Package, Boxes, MapPin, Box, Trophy, Warehouse, Activity, Radio } from "lucide-react";
import { useLang } from "@/context/LanguageContext";

// Phase D (Feb 2026): Auto-rotating screensaver-style carousel for Management Dashboard.
// Phase G (Feb 2026): + Depot Stock / On-Road Stock / Live Tracking slides.
const SLIDES = [
  { variant: "orders",       titleKey: "orders",            icon: Truck,        accent: "from-[#0055FF] to-[#0A1A3F]", numKey: "total_orders" },
  { variant: "delivered",    titleKey: "delivered_today",   icon: CheckCircle2, accent: "from-[#34C759] to-[#1E8E3E]", numKey: "delivered" },
  { variant: "pending",      titleKey: "pending",           icon: Package,      accent: "from-[#FF9500] to-[#CC7700]", numKey: "pending" },
  { variant: "boxes",        titleKey: "boxes_sold",        icon: Boxes,        accent: "from-[#5856D6] to-[#3F3D9C]", numKey: "units_sold" },
  { variant: "districts",    titleKey: "district_wise_sold",icon: MapPin,       accent: "from-amber-500 to-amber-700", listKey: "by_district" },
  { variant: "products",     titleKey: "product_wise_sold", icon: Box,          accent: "from-[#0055FF] to-[#5856D6]", listKey: "by_product" },
  { variant: "performance",  titleKey: "best_sales_today",  icon: Trophy,       accent: "from-[#0A1A3F] to-[#0055FF]", listKey: "performance" },
  // Phase G:
  { variant: "depot-stock",  titleKey: "depot_stock",       icon: Warehouse,    accent: "from-cyan-600 to-cyan-800",   listKey: "depot_by_product",  altNumKey: "depot_total" },
  { variant: "on-road-stock",titleKey: "on_road_stock",     icon: Truck,        accent: "from-emerald-600 to-emerald-800", listKey: "on_road",         altNumKey: "on_road_total" },
  { variant: "live-tracking",titleKey: "live_tracking",     icon: Activity,     accent: "from-rose-600 to-rose-800",   listKey: "live_users",         altNumKey: "active_count" },
];

export default function ManagementCarousel({ today = {}, byDistrict = [], byProduct = [], performance = [], depotStock = null, liveTracking = null }) {
  const nav = useNavigate();
  const { t, lang } = useLang();
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  const intervalRef = useRef(null);

  useEffect(() => {
    if (paused) return;
    intervalRef.current = setInterval(() => {
      setIdx(i => (i + 1) % SLIDES.length);
    }, 5000); // 5s per slide — calm professional rhythm
    return () => clearInterval(intervalRef.current);
  }, [paused]);

  // Pause when tab not visible (saves CPU + avoids confusing jump-back when user returns)
  useEffect(() => {
    const onVisibility = () => {
      if (document.hidden) clearInterval(intervalRef.current);
      else if (!paused) {
        intervalRef.current = setInterval(() => setIdx(i => (i + 1) % SLIDES.length), 5000);
      }
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
  }, [paused]);

  const cur = SLIDES[idx];
  const Icon = cur.icon;

  const onTap = () => {
    if (!paused) { setPaused(true); return; }
    // paused → open drilldown / live page
    if (cur.variant === "depot-stock")     { nav("/management/depot-stock"); return; }
    if (cur.variant === "on-road-stock")   { nav("/management/stock-report"); return; }
    if (cur.variant === "live-tracking")   { nav("/management/live-tracking"); return; }
    nav(`/management/${cur.variant}`);
  };

  // Build mini table content
  let mainNumber = 0;
  let rows = [];
  // Tier 2 (Feb 2026): Tamil-aware display name for any list row.
  const _disp = (it) => (lang === "ta" && (it?.name_ta)) ? it.name_ta : (it?.name || "");
  if (cur.numKey) {
    mainNumber = today[cur.numKey] || 0;
  }
  if (cur.listKey === "by_district") {
    rows = (byDistrict || []).slice(0, 5).map(d => ({ name: _disp(d), value: d.units_sold || 0 }));
    mainNumber = rows.reduce((s, r) => s + r.value, 0);
  } else if (cur.listKey === "by_product") {
    rows = (byProduct || []).slice(0, 5).map(p => ({ name: _disp(p), value: p.units || 0 }));
    mainNumber = rows.reduce((s, r) => s + r.value, 0);
  } else if (cur.listKey === "performance") {
    const mgrSuffix = lang === "ta" ? " (மே)" : " (M)";
    rows = (performance || []).slice(0, 5).map(p => ({ name: _disp(p) + (p.role === "manager" ? mgrSuffix : ""), value: p.units_sold || 0 }));
    mainNumber = rows.reduce((s, r) => s + r.value, 0);
  } else if (cur.listKey === "depot_by_product") {
    rows = (depotStock?.by_product || []).slice(0, 5).map(p => ({ name: _disp(p), value: p.qty || 0 }));
    mainNumber = depotStock?.depot_total || 0;
  } else if (cur.listKey === "on_road") {
    const mgrSuffix = lang === "ta" ? " (மே)" : " (M)";
    rows = (depotStock?.on_road || []).slice(0, 5).map(p => ({ name: _disp(p) + (p.role === "manager" ? mgrSuffix : ""), value: p.remaining }));
    mainNumber = depotStock?.on_road_total || 0;
  } else if (cur.listKey === "live_users") {
    const users = (liveTracking?.users || []).slice(0, 5);
    rows = users.map(u => ({ name: _disp(u), value: u.today_boxes || 0 }));
    mainNumber = liveTracking?.counts?.active || 0;
  }

  return (
    <div
      data-testid="mg-carousel"
      onClick={onTap}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") onTap(); }}
      className={`relative bg-gradient-to-br ${cur.accent} text-white rounded-3xl p-6 sm:p-8 shadow-xl cursor-pointer select-none overflow-hidden transition-transform active:scale-[0.99]`}
    >
      {/* Pause/Play indicator */}
      <div className="absolute top-4 right-4 flex items-center gap-2">
        <div className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full ${paused ? "bg-white/20" : "bg-white/10"}`}>
          {paused ? (lang === "ta" ? "தட்டி பாரு" : "Tap to open") : (lang === "ta" ? "தட்டி நிறுத்து" : "Tap to pause")}
        </div>
        <div className="w-8 h-8 rounded-full bg-white/15 grid place-items-center">
          {paused ? <Play className="w-4 h-4" /> : <Pause className="w-4 h-4" />}
        </div>
      </div>

      {/* Main content */}
      <div className="flex items-start gap-4 mb-4">
        <div className="w-12 h-12 rounded-2xl bg-white/15 grid place-items-center shrink-0">
          <Icon className="w-6 h-6" />
        </div>
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-[0.2em] opacity-80 font-bold">{t(cur.titleKey)}</div>
          <div className="heading-font text-4xl font-bold mt-0.5" data-testid="mg-carousel-number">{mainNumber}</div>
          <div className="text-xs opacity-80 mt-0.5">{cur.listKey ? `Top 5 — ${t("boxes")}` : t("boxes")}</div>
        </div>
      </div>

      {/* Mini table */}
      {rows.length > 0 && (
        <div className="space-y-1.5 mt-2">
          {rows.map((r, i) => (
            <div key={i} className="flex items-center justify-between gap-2 bg-white/10 rounded-xl px-3 py-2 text-sm">
              <span className="font-semibold truncate">{i + 1}. {r.name}</span>
              <span className="font-bold tabular-nums">{r.value}</span>
            </div>
          ))}
        </div>
      )}

      {/* Progress dots */}
      <div className="flex items-center justify-center gap-1.5 mt-5">
        {SLIDES.map((_, i) => (
          <div
            key={i}
            className={`h-1 rounded-full transition-all ${i === idx ? "w-8 bg-white" : "w-2 bg-white/30"}`}
          />
        ))}
      </div>
    </div>
  );
}
